import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FileText, Loader2, Info, X, AlertTriangle } from 'lucide-react';
import { fetchPersonalEmpleados } from '../Services/personal.services';
import { getFirmas } from '../parametrizacion/Services/parametrizacion.services';
import { generarCertificadoLaboral } from './Services/documentos-personal.services';
import { useToast } from '@/components/ui/use-toast';

// Centinela para "sin firma". Radix Select no admite value="" en un SelectItem
// y value={undefined} deja el componente sin controlar en el primer render.
const SIN_FIRMA = '__sin_firma__';

const CertificadoLaboralModal = ({ open, onOpenChange, onGenerado }) => {
  const { toast } = useToast();
  const [empleados, setEmpleados] = useState([]);
  const [firmas, setFirmas] = useState([]);
  const [empleado, setEmpleado] = useState(null);
  const [firmaId, setFirmaId] = useState('');
  const [incluirSalario, setIncluirSalario] = useState(true);
  const [conPeriodo, setConPeriodo] = useState(false);
  const [periodoDesde, setPeriodoDesde] = useState('');
  const [periodoHasta, setPeriodoHasta] = useState('');
  const [lugar, setLugar] = useState('');
  const [generando, setGenerando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [errorCarga, setErrorCarga] = useState(null);

  useEffect(() => {
    if (!open) return;

    let cancelado = false;
    setCargando(true);
    setErrorCarga(null);

    const cargar = async () => {
      // allSettled y no Promise.all: si el listado de firmas falla —por
      // ejemplo porque el backend no se reinició y /firmas responde 404— el
      // de empleados tiene que seguir cargando igual. Con Promise.all un solo
      // fallo abortaba ambos y el selector quedaba vacío sin explicación.
      const [resEmp, resFir] = await Promise.allSettled([
        fetchPersonalEmpleados(),
        getFirmas(),
      ]);
      if (cancelado) return;

      if (resEmp.status === 'fulfilled') {
        setEmpleados(resEmp.value.data || []);
      } else {
        setErrorCarga(`No se pudieron cargar los empleados: ${resEmp.reason?.message}`);
      }

      if (resFir.status === 'fulfilled') {
        const lista = resFir.value?.data || [];
        setFirmas(lista);
        // Si solo hay una firma activa, se propone de una: es lo habitual y
        // ahorra un paso en cada emisión.
        const activas = lista.filter((f) => f.activo);
        if (activas.length === 1) setFirmaId(String(activas[0]._id));
      } else {
        // Las firmas son opcionales: el certificado se emite igual sin ella.
        setFirmas([]);
      }

      setCargando(false);
    };

    cargar();
    return () => {
      cancelado = true;
    };
  }, [open]);

  useEffect(() => {
    if (open) return;
    setEmpleado(null);
    setFirmaId('');
    setIncluirSalario(true);
    setConPeriodo(false);
    setPeriodoDesde('');
    setPeriodoHasta('');
    setLugar('');
  }, [open]);

  const generar = async (e) => {
    e.preventDefault();
    if (!empleado) {
      toast({
        title: 'Falta el empleado',
        description: 'Selecciona el empleado del certificado.',
        variant: 'destructive',
      });
      return;
    }
    setGenerando(true);
    try {
      const res = await generarCertificadoLaboral({
        empleadoId: empleado._id,
        firmaId: firmaId || undefined,
        incluirSalario,
        conPeriodo,
        periodoDesde: conPeriodo ? periodoDesde || undefined : undefined,
        periodoHasta: conPeriodo ? periodoHasta || undefined : undefined,
        lugar: lugar || undefined,
      });

      window.open(res.url, '_blank', 'noopener,noreferrer');

      toast({
        title: 'Certificado generado',
        description: `Se guardó en el expediente de ${res.nombreEmpleado}.`,
      });
      onGenerado?.();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'No se pudo generar',
        description:
          err.response?.data?.message || err.message || 'Ocurrió un problema.',
        variant: 'destructive',
      });
    } finally {
      setGenerando(false);
    }
  };

  const firmasActivas = firmas.filter((f) => f.activo);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generar certificado laboral</DialogTitle>
          <DialogDescription>
            El PDF se genera en el servidor, se guarda en el expediente del empleado y queda
            disponible para que lo descargue desde su portal.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={generar} className="space-y-4">
          {/* Un fallo de carga no puede quedar solo en un toast que se pierde:
              si los empleados no llegan, el combobox se ve vacío y parece un
              bug. Aquí se dice qué pasó. */}
          {errorCarga && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <p className="text-sm text-destructive">{errorCarga}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="cert-empleado">Empleado</Label>
            {/* <select> nativo y no un Popover/combobox de Radix. Dentro de un
                Dialog modal, cualquier overlay de Radix que se monte en un
                portal queda fuera del FocusScope y del react-remove-scroll del
                Dialog: se cierra antes de que el clic alcance a las opciones y
                nunca se selecciona nada. El <select> nativo no usa portales ni
                capas, funciona igual dentro y fuera del modal y el navegador
                permite filtrar escribiendo la cédula o el nombre. */}
            <select
              id="cert-empleado"
              value={empleado?._id || ''}
              onChange={(e) =>
                setEmpleado(
                  empleados.find((x) => x._id === e.target.value) || null
                )
              }
              disabled={cargando}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">
                {cargando ? 'Cargando empleados…' : 'Selecciona un empleado…'}
              </option>
              {empleados.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.nombre} {emp.apellido}
                  {emp.cedula ? ` — ${emp.cedula}` : ''}
                  {emp.rol ? ` — ${emp.rol}` : ''}
                </option>
              ))}
            </select>
            {!cargando && empleados.length === 0 && !errorCarga && (
              <p className="text-xs text-muted-foreground">
                No hay empleados para mostrar.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="cert-firma">Firma</Label>
            {/* Radix lanza una excepción si un SelectItem tiene value="". Por eso
                "Sin firma" es un valor centinela real y no undefined: dejarlo
                en undefined hacía que el Select arrancara sin controlar y al
                cargar la firma quedara controlado, ese cambio es lo que dispara
                el warning "changing from uncontrolled to controlled" y lo que
                dejaba el campo mostrando "Sin firma" con la firma ya cargada. */}
            <div className="flex gap-2">
              <Select value={firmaId || SIN_FIRMA} onValueChange={(v) => setFirmaId(v === SIN_FIRMA ? '' : v)}>
                <SelectTrigger id="cert-firma" className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SIN_FIRMA}>Sin firma</SelectItem>
                  {firmasActivas.map((f) => (
                    <SelectItem key={f._id} value={String(f._id)}>
                      {f.nombre}
                      {f.cargo ? ` — ${f.cargo}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {firmaId && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  title="Quitar firma"
                  onClick={() => setFirmaId('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            {firmasActivas.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Aún no hay firmas cargadas. Agrégalas en Parametrización → Firmas.
              </p>
            )}
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="pr-4">
              <Label htmlFor="cert-salario" className="text-sm">
                Incluir la remuneración
              </Label>
              <p className="text-xs text-muted-foreground">
                Declara el salario que devenga el empleado. Apágalo si el certificado no debe
                mostrar cifras.
              </p>
            </div>
            <Checkbox
              id="cert-salario"
              checked={incluirSalario}
              onCheckedChange={setIncluirSalario}
            />
          </div>

          <div className="space-y-3 rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="cert-periodo" className="text-sm">
                Acotar un período
              </Label>
              <Checkbox
                id="cert-periodo"
                checked={conPeriodo}
                onCheckedChange={setConPeriodo}
              />
            </div>
            {conPeriodo && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cert-desde" className="text-xs text-muted-foreground">
                    Desde
                  </Label>
                  <Input
                    id="cert-desde"
                    type="date"
                    value={periodoDesde}
                    onChange={(e) => setPeriodoDesde(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cert-hasta" className="text-xs text-muted-foreground">
                    Hasta
                  </Label>
                  <Input
                    id="cert-hasta"
                    type="date"
                    value={periodoHasta}
                    onChange={(e) => setPeriodoHasta(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="cert-lugar">Lugar de expedición</Label>
            <Input
              id="cert-lugar"
              value={lugar}
              onChange={(e) => setLugar(e.target.value)}
              placeholder="Ej. Medellín"
            />
          </div>

          <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
            <Info className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground">
              Los datos salen del maestro de empleados. Si el cargo o la fecha de ingreso no
              aparecen correctos, corrígelos en el listado de personal antes de emitir.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={generando || !empleado}>
              {generando ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-2 h-4 w-4" />
              )}
              Generar certificado
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CertificadoLaboralModal;